import React, { useState, useEffect } from "react";
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineArrowTrendingUp,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
} from "react-icons/hi2";
import { authService } from "../../services";
import masterDataService from "../../services/masterDataService";
import SharedModal from "../../components/common/SharedModal";
import PageLoading from "../../components/common/PageLoading";

const ImpactLevels = () => {
  const [impactLevels, setImpactLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingImpactLevel, setEditingImpactLevel] = useState(null);
  const [formData, setFormData] = useState({
    impactLevel: "",
    weightage: "",
    status: "Active",
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadImpactLevels();
    } else {
      setError("Please login to view impact levels");
      setLoading(false);
    }
  }, [tenantId]);

  const loadImpactLevels = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await masterDataService.getImpactLevels(tenantId);
      setImpactLevels(data);
    } catch (err) {
      setError("Failed to load impact levels. Please try again.");
      console.error("Error loading impact levels:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredImpactLevels = impactLevels.filter((il) =>
    il.impactLevel?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const handleAdd = () => {
    setEditingImpactLevel(null);
    setFormData({
      impactLevel: "",
      weightage: "",
      status: "Active",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (impactLevel) => {
    setEditingImpactLevel(impactLevel);
    setFormData({
      impactLevel: impactLevel.impactLevel || "",
      weightage: impactLevel.weightage || 0,
      status: impactLevel.status || "Active",
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError("");
      await masterDataService.deleteImpactLevel(id);
      await loadImpactLevels();
      setDeleteConfirm(null);
    } catch (err) {
      setError("Failed to delete impact level. Please try again.");
      console.error("Error deleting impact level:", err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.impactLevel.trim()) {
      errors.impactLevel = "Impact level is required";
    } else if (formData.impactLevel.length > 20) {
      errors.impactLevel = "Impact level must be less than 20 characters";
    }

    // Weightage validations
    const weightVal = parseFloat(formData.weightage);
    if (
      formData.weightage === "" ||
      formData.weightage === null ||
      formData.weightage === undefined
    ) {
      errors.weightage = "Weightage is required";
    } else if (isNaN(weightVal)) {
      errors.weightage = "Weightage must be a number";
    } else if (!Number.isInteger(weightVal)) {
      errors.weightage = "Weightage must be an integer (no decimals)";
    } else if (weightVal < 1 || weightVal > 10) {
      errors.weightage = "Weightage must be between 1 and 10";
    } else {
      // Duplicate check
      const isDuplicate = impactLevels.some(
        (il) =>
          parseInt(il.weightage) === weightVal &&
          il.impactId !== editingImpactLevel?.impactId,
      );
      if (isDuplicate) {
        errors.weightage = "Weightage already exists";
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError("");

      const impactLevelData = {
        tenantId: tenantId,
        impactLevel: formData.impactLevel.trim(),
        weightage: parseInt(formData.weightage) || 0,
        status: formData.status,
      };

      if (editingImpactLevel) {
        impactLevelData.impactId = editingImpactLevel.impactId;
        await masterDataService.updateImpactLevel(
          editingImpactLevel.impactId,
          impactLevelData,
        );
      } else {
        await masterDataService.createImpactLevel(impactLevelData);
      }

      await loadImpactLevels();
      setShowModal(false);
      setFormData({ impactLevel: "", weightage: "", status: "Active" });
      setEditingImpactLevel(null);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.response?.data?.errors ||
        err.message ||
        "Failed to save impact level. Please try again.";
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingImpactLevel(null);
    setFormData({ impactLevel: "", weightage: "", status: "Active" });
    setFormErrors({});
  };

  if (loading) {
    return <PageLoading message="Loading impact levels..." />;
  }

  return (
    <div className="impact-levels-page">
      <div className="page-header">
        <div className="header-content">
          <h2>Impact Levels</h2>
          <p>Manage impact level classifications and weightages</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus className="btn-icon" />
          Add Impact Level
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search impact levels..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredImpactLevels.length}{" "}
            {filteredImpactLevels.length === 1
              ? "impact level"
              : "impact levels"}
          </div>
        </div>

        {filteredImpactLevels.length === 0 ? (
          <div className="empty-state">
            <HiOutlineArrowTrendingUp className="empty-icon" />
            <h3>No impact levels found</h3>
            <p>
              {searchTerm
                ? "Try adjusting your search terms"
                : "Get started by creating your first impact level"}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Impact Level
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Impact Level</th>
                <th>Weightage</th>
                <th>Status</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredImpactLevels.map((il, index) => (
                <tr key={il.impactId}>
                  <td>{index + 1}</td>
                  <td className="name-cell">{il.impactLevel}</td>
                  <td>{il.weightage}</td>
                  <td>
                    <span
                      className={`status-badge ${il.status?.toLowerCase()}`}
                    >
                      {il.status || "Active"}
                    </span>
                  </td>
                  <td>
                    {il.createdAt
                      ? new Date(il.createdAt).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(il)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(il)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingImpactLevel ? "Edit Impact Level" : "New Impact Level"}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={handleCloseModal} disabled={submitting}>Cancel</button>
            <button type="submit" form="impact-level-form" className="btn-primary" disabled={submitting}>
              {submitting ? (editingImpactLevel ? "Updating..." : "Creating...") : (editingImpactLevel ? "Update" : "Create")}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="impact-level-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="impactLevel">
                Impact Level <span className="required">*</span>
              </label>
              <input
                type="text"
                id="impactLevel"
                value={formData.impactLevel}
                onChange={(e) =>
                  setFormData({ ...formData, impactLevel: e.target.value })
                }
                className={formErrors.impactLevel ? "error" : ""}
                placeholder="e.g., Critical, High, Medium, Low, Minimal"
                maxLength={20}
              />
              {formErrors.impactLevel && (
                <span className="field-error">{formErrors.impactLevel}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="weightage">Weightage</label>
              <input
                type="number"
                id="weightage"
                value={formData.weightage}
                onChange={(e) =>
                  setFormData({ ...formData, weightage: e.target.value })
                }
                className={formErrors.weightage ? "error" : ""}
                placeholder="e.g., 1-10"
                min="1"
                max="10"
                step="1"
              />
              {formErrors.weightage && (
                <span className="field-error">{formErrors.weightage}</span>
              )}
              <small
                style={{ color: "var(--text-tertiary)", fontSize: "0.85rem" }}
              >
                Numeric weight for prioritization
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.impactId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{" "}
            <strong>{deleteConfirm?.impactLevel}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone. The impact level will be marked as
            inactive.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default ImpactLevels;
